import React from 'react';
import _ from 'lodash';

import {
  GenerateLineItemSkuSwapInput,
  ItemFulfillmentWidgetDocument,
  useGenerateSkuSwapMutation,
  useSkuSwapModalQuery
} from 'generated/graphql';

import { levenshteinEditDistance } from 'levenshtein-edit-distance';

import * as types from './types';



export interface IFulfilledLineItem {
  id: string;
  sku: string;
  serialNumber: string;
}

export interface IData {
  lineItems: IFulfilledLineItem[];
  formState: types.IFormState;
}

export function useQuery(orderId: string) {
  const tuple = useSkuSwapModalQuery({
    variables: {
      orderId
    }
  });

  return React.useMemo(
    () => {
      const order = tuple.data?.order;
      const fulfillmentLineItems = order?.itemFulfillment?.lineItems;
      const lineItems = _.filter(order?.lineItems, li => {
        const correspondingFulfilledLineItem = _.find(fulfillmentLineItems, fli => fli.item.sku === li.product.sku);
        return _.isNil(correspondingFulfilledLineItem);
      });

      const data: IData = {
        lineItems: _.map(fulfillmentLineItems, (li): IFulfilledLineItem => ({
          id: li.id,
          sku: li.item.sku,
          serialNumber: _.first(li.assignments)?.inventoryNumber ?? ''
        })),
        formState: {
          lineItems: _.map(lineItems ?? [], (lineItem): types.ILineItem => {
            const hasSku = _.find(fulfillmentLineItems, li => li.item.sku === lineItem.product.sku);
            const editDistances = _.map(
              fulfillmentLineItems,
              fulfillmentLineItem => ({
                similarityFactor: levenshteinEditDistance(fulfillmentLineItem.item.sku, lineItem.product.sku),
                sku: fulfillmentLineItem.item.sku
              })
            );

            const suggestedLineItem = hasSku ?
              null :
              _.first(
                _.sortBy(
                  _.filter(editDistances, ed => ed.similarityFactor < 10), ed => ed.similarityFactor));

            return {
              lineItemId: lineItem.id,
              purchasedProductName: lineItem.product.name,
              purchasedProductSku: lineItem.product.sku,
              selectedProductId: lineItem.product.id,
              purchasedQuantity: lineItem.quantity,
              suggestedSku: suggestedLineItem?.sku ?? '',
              swappedForProduct: null,
              quantity: 1
            };
          })
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useGenerateSkuSwap(orderId: string) {
  const [fnMutate, tuple] = useGenerateSkuSwapMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ItemFulfillmentWidgetDocument,
        variables: { orderId }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      const specifiedLineItems = _.filter(
        formState.lineItems, lineItem =>
          !_.isNil(lineItem.swappedForProduct) && lineItem.quantity > 0
      );

      await fnMutate({
        variables: {
          input: {
            lineItems: _.map(specifiedLineItems, (lineItem): GenerateLineItemSkuSwapInput => ({
              lineItemId: lineItem.lineItemId,
              swappedProductId: lineItem.swappedForProduct?.id ?? '',
              quantity: _.toNumber(lineItem.quantity)
            }))
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
